/* eslint-disable max-lines */
import {
  AURORA_DESIGN_REFRESH_DISCLOSURE,
  AURORA_DESIGN_REFRESH_STARTED_CONFIRMATION,
} from "~/constants/aurora-design-refresh.constants"
import {
  SOLAR_AI_COMPUTE_BONUS,
  SOLAR_AI_COMPUTE_FAQ,
  SOLAR_AI_COMPUTE_HOW_IT_WORKS,
  SOLAR_AI_COMPUTE_STORY,
} from "~/constants/solar-ai-compute-copy.constants"

export const utilityCompanyAdminTable = {
  website: `Website`,
  name: `Name`,
  offeringType: `Offering Type`,
  serviceTypes: `Service Types`,
  ownership: `Ownership`,
}

export const regressionWarnings = {
  regressionWarning_EdgeDesignation: `Metadata will be reset on any roof faces you modify.`,
  // regressionWarning_PanelPlacement: `All panels will be removed.`,
  // regressionWarning_PanelActivation: `Active panels will be reset.`,
}

export const toolDescriptions = {
  drawToolDesc: `Draw lines of a roof face.`,
  edgeDesignatorDesc: `Designate an edge of a roof face.`,
  eraseToolDesc: `Erase an object.`,
  groundMountToolDesc: `Use to mount panels to the ground`,
  panelActivatorDesc: `Click to enable or disable selected panels.`,
  selectDesc: `Click to interact with an object (roof, edge, panel).`,
  panelCreatorLandscapeDesc: `Add landscape-oriented panels to your system.`,
  panelCreatorPortraitDesc: `Add portrait-oriented panels to your system.`,
  panelEraserDesc: `Remove panels from your system.`,
  treeToolDesc: `Use to create trees`,
  panDesc: `Move the map view.`,
  moveDesc: `Move all design objects as one.`,
  rulerDesc: `Measure distances between two points.`,
}

export const toolLabels = {
  draw: `Draw`,
  edgeDesignator: `Edge Designator`,
  erase: `Erase`,
  groundMount: `Ground Mount`,
  panelActivator: `Panel Activator`,
  panelCreatorLandscape: `Panel Creator (Landscape)`,
  panelCreatorPortrait: `Panel Creator (Portrait)`,
  panelEraser: `Panel Eraser`,
  select: `Select`,
  trees: `Trees`,
  pan: `Pan`,
  move: `Move`,
  ruler: `Ruler`,
}

export const poisonMessages = {
  poisonedQuote: `Something went wrong while generating a quote. Try changing the design or loan product.`,
  poisonedEstimate: `Something went wrong while estimating production for an install area. Try changing the design.`,
  poisonedSavings: `Something went wrong while projecting savings. Try changing the design.`,
}

export const brochureSections = {
  "monthlyBill": `Monthly Bill`,
  "processSummary": `Process Summary`,

  // finance section
  "paymentSummaryDes": `your financial summary`,
  "paymentSummaryHeader": `Flexible Payment Options`,
  "yourPayments": `Your Payments`,
  "monthlyPayment": `Monthly Payment`,
  "estimatedVppMonthlyBenefit": `Est. VPP monthly benefit:`,
  "netEffectivePayment": `Net effective payment:`,
  "vppPaymentsEstimatedVariable": `VPP payments are estimated and variable`,
  "financialBreakdown": `Financial Breakdown`,
  "startingSystemPrice": `Starting System Price`,
  "systemPrice": `System Price`,
  "propelDiscount": `Propel Discount`,
  "discount": `Discount`,
  "participateNetAmountDue": `Participate Net Amount Due`,
  "yourFinancedAmount": `Your Financed Amount`,
  "yourFinancedAmountNote": `This is the amount you'll finance after your Propel discount is applied.`,
  "concertFinancingFinePrint": `Unlike utility bills that rise every year, your Propel payment stays the same. Here's how it works: your Propel discount is applied immediately at signing, reducing your total contract value before a single dollar is financed. Your loan is based on that lower amount - giving you a locked-in monthly payment at a competitive rate for the full {{term}}-year term. No escalator. No rate surprises. Just predictable energy costs and long-term savings. Final terms will be outlined in your loan agreement.`,
  "applyIncentives": `apply incentives to loan`,
  "incentivesAppliedToLoanNote": `Checkboxes indicate that incentives are being applied to the loan.`,
  "incentiveFirstYearValueFootnote": `* The incentive value represents only the first year's value. Future years' values will be applied as cash.`,
  "energyRebatesDisclaimerTitle": `Energy Rebates and Tax Credits.`,
  "energyRebatesDisclaimerContent": `Upon Your request, Powur will provide You with reasonable support and documentation that may be necessary in connection with Your application for any federal, state, or local energy rebate or tax credit available in connection with installation of the Solar System; provided however that POWUR MAKES NO REPRESENTATION, WARRANTY, OR GUARANTEE THAT YOU WILL QUALIFY FOR OR WILL OBTAIN ANY FEDERAL, STATE, OR LOCAL ENERGY REBATE OR TAX CREDIT IN CONNECTION WITH THE SOLAR SYSTEM. You are advised to consult legal and/or accounting professionals regarding Your eligibility for and assistance with any such energy rebate or tax credit. Powur is not responsible for changes in availability or incentives of any federal, state, or local energy rebate program or tax credit.`,
  "financeOption": `Finance Option`,
  "financePartner.goodleap": `GoodLeap`,
  "financePartner.sungage": `Sungage`,
  "financePartner.enfin": `Enfin`,
  "financePartner.mosaic": `Mosaic`,
  "financePartner.sunlightFinancial": `Sunlight Financial`,
  "financePartner.lightReach": `LightReach`,
  "financePartner.concertFinancial": `Concert Financial`,
  "financePartner.concertBattery": `Concert (Battery Only)`,
  "financePartner.creditHuman": `Credit Human`,
  "financePartner.creditHumanBattery": `Credit Human (Battery Only)`,
  "financePartner.sungageBattery": `Sungage (Battery Only)`,
  "financePartner.lightReachBattery": `LightReach (Battery Only)`,
  "financePartnerFundingGroup.goodleapTpo": `Goodleap - TPO`,
  "financePartnerFundingGroup.goodleapLoan": `Goodleap - Loan`,
  "financePartnerFundingGroup.enfinTpo": `Enfin - TPO`,
  "financePartnerFundingGroup.sungageBrightStar": `Sungage - BrightStar`,
  "financePartnerFundingGroup.sungageSunriseEscalator": `Sungage - Sunrise Escalator`,
  "financePartnerFundingGroup.sungageBatteryBrightStart": `Sungage - BrightStart (Battery Only)`,
  "financePartnerFundingGroup.sungageBatterySunrise": `Sungage - Sunrise (Battery Only)`,

  // environmental impact section
  "environmentalImpact": `Environmental Impact`,
  "environmentalAndHomeValueImpact": `Environmental and Home Value Impact`,
  "co2Label": `Metric tons of CO2 offset over 25 Years*`,
  "treesLabel": `Equivalent Number of Trees Planted`,
  "valueLabel": `Estimated Value Added to Your Home*`,
}

const systemSummarySlide = {
  moduleType: `Module Type`,
  numberOfPanels: `Number of Panels`,
}

const monthlyBillSlideDisclaimers = {
  postSolarUtilityCalculationDisclaimer: `Disclaimer: Estimated 25-year savings and post-solar utility bills are based on assumed battery self-consumption and may vary due to individual consumption habits and potential battery degradation over time.`,
  monthlyBillDisclaimer: `Utility bills and rate escalation are estimates based on historical data, and actual rates may vary. This proposal provides non-binding estimates. While we strive for accuracy, no warranties or guarantees are provided regarding rate increases, utility costs, or the solar system’s ability to save on electricity bills.`,
  deregulatedRatesNotice: `In deregulated electric markets, the proposal uses an estimated contracted rate for average utility bills. This rate may change with market conditions, provider decisions, or changes in contracted terms.`,
}

export const utilitySavingsSlides = {
  doNothing: `Do Nothing`,
  goSolar: `Go Solar`,
  dialYear: `Year`,
  dialYears: `Years`,
  dialSavings: `25-Year Savings`,
  averageUtilityBill: `Average Utility Bill`,
  newAverageUtilityBill: `New Average Utility Bill`,
  solarPayment: `Solar Payment`,
  billEnergy: `Energy`,
  billFixed: `Fixed`,
  billTotal: `Total`,
  recalculatingUtilitySavings: `Recalculating utility savings`,
  effectiveUtilityRate: `Effective Utility Rate:`,
  annualUtilityIncrease: `Annual Utility Increase:`,
  annualEnergyConsumption: `Annual Energy Consumption:`,
  yearOneSolarRate: `Year 1 Solar Rate`,
  averageSolarRate: `Average Solar Rate:`,
  annualSolarIncrease: `Annual Solar Increase:`,
  annualSolarProduction: `Annual Solar Production:`,
  perMonth: `/mo`,
  currentAverageUtility: `Current Average Utility`,
  currentAverageUtilityWithSolar: `Current Average Utility With Solar`,
  averageUtilityIn25Years: `Average Utility in 25 Years`,
  averageUtilityWithSolarIn25Years: `Average Utility With Solar in 25 Years`,
  costOfDoingNothingOver25Years: `Cost Of Doing Nothing Over 25 Years`,
  savingsWithSolarOver25Years: `Savings With Solar Over 25 Years`,
  graphYears: `Years`,
  avgMonthlyPayment: `Avg Monthly Payment`,
  graphCostWithSolar: `Cost With Solar`,
  graphSolarSystemCost: `Solar System Cost`,
  graphCostWithoutSolar: `Cost Without Solar`,
}

const shareProposalStep = {
  presentationProductionUnavailable: `Production estimates are unavailable for this presentation. Ask your seller for an updated proposal.`,
  presentationSavingsUnavailable: `Utility savings are unavailable for this presentation. Ask your seller for an updated proposal.`,
  publicPresentationDescription: `Anyone with this link can view the proposal presentation without signing in. The link does not allow changes to the proposal.`,
  publicPresentationLink: `Public presentation link`,
  loadingPresentationLink: `Loading presentation link…`,
  presentationLinkError: `Unable to load or create the presentation link. Please try again.`,
  presentationLinkExpires: `Link expires {{date}}.`,
  presentationLinkCopied: `Copied`,
  copyPresentationLink: `Copy link`,
  openPresentation: `Open presentation`,
  presentationLinkCopyError: `Unable to copy automatically. Select and copy the link above.`,
  creatingPresentationLink: `Creating link…`,
  createPresentationLink: `Create public link`,

  pleasePublish: `Please publish your proposal before sharing.`,
}

const en = {
  translation: {
    jan: `Jan`,
    feb: `Feb`,
    mar: `Mar`,
    apr: `Apr`,
    may: `May`,
    jun: `Jun`,
    jul: `Jul`,
    aug: `Aug`,
    sep: `Sep`,
    oct: `Oct`,
    nov: `Nov`,
    dec: `Dec`,

    activeKey: `Active Key`,
    allPermissions: `All permissions`,
    all: `All`,
    concertUsers: `Concert`,
    currentKey: `Current Key`,
    filterByPermission: `Filter by Permission`,
    licenseExists: `License Exists`,
    loadingConcertUsers: `Loading Concert users…`,
    loadingIntegration: `Loading integration…`,
    loadingServiceIntegrations: `Loading service integrations…`,
    loadingUser: `Loading user…`,
    loadingUsers: `Loading users…`,
    noConcertUsersFound: `No Concert users found.`,
    noServiceIntegrationsFound: `No service integrations found.`,
    noUsersFound: `No users found.`,
    onboardingCompleted: `Onboarding Completed`,
    permissionsUnavailable: `Unable to load permission filters. Please try again.`,
    powurUsers: `Powur Users`,
    searchPermissions: `Search permissions`,
    unableCreateIntegration: `Unable to create this integration. Please try again.`,
    unableLoadIntegration: `Unable to load this integration. Please try again.`,
    unableLoadServiceIntegrations: `Unable to load service integrations. Please try again.`,
    unableLoadUser: `Unable to load this user. Please try again.`,
    unableLoadUsers: `Unable to load users. Please try again.`,
    unableSaveIntegration: `Unable to save these changes. Please try again.`,
    unableUpdateUser: `Unable to update this user. Please try again.`,
    users: `Users`,
    add: `Add`,
    adder: `Adder`,
    adders: `Adders`,
    systemPricingOverview: `System Pricing`,
    systemPricingOverviewDesc: `Overview of the system pricing for this project.`,
    addIntegration: `New Integration`,
    address: `Address`,
    addressSearchPlaceholder: `Search for your address...`,
    admin: `Admin`,
    allAdders: `All Adders`,
    allPanels: `All Panels`,
    annualCost: `Annual Utility Cost ($)`,
    annualConsumption: `Annual Consumption`,
    annualProduction: `Annual Production`,
    annualUsage: `Annual Usage`,
    annualUsageDesc: `I know about how much power is used in a year.`,
    annualUsageProfile: `Annual Usage Profile`,
    annualUsageProfileDesc: `This estimate of annual consumption was generated using the readings you provided.`,
    apiKey: `API Key`,
    apiKeys: `API Keys`,
    applyDefault: `Apply Default`,
    applyToLoan: `Apply To Loan`,
    approximateAnnualUsage: `Approximate Annual Usage`,
    autoPlacePanels: `Auto-place Panels`,
    averageMonthCost: `Average Monthly Cost`,
    averageMonthCostProfileDesc: `This estimate of average monthly cost was generated using the readings you provided.`,
    averageMonthlyCost: `Average Monthly Cost`,
    azimuth: `Azimuth`,
    backToPowur: `Back to Powur.com`,
    backToProposal: `Back to Proposal`,
    backToQueue: `Back to Queue`,
    batteryCompatibility: `Battery compatibility?`,
    batteryInstallation: `Battery Installation`,
    both: `Both`,
    bothMountsDesc: `Having both ground and roof solar systems can be beneficial for smaller roof surfaces. This selection is optimal for homeowners that want to maximize solar output.`,
    browseFiles: `Browse Files`,
    cancel: `Cancel`,
    cancellationFees: `Cancellation Fees`,
    cashPrice: `Cash Price`,
    category: `Category`,
    clear: `Clear`,
    chooseAModuleToSeeDetails: `Choose a module to see details`,
    consumption: `Consumption`,
    contactEmail: `Contact Email`,
    contactName: `Contact Name`,
    contained: `Contained`,
    continue: `Continue`,
    continueConfirmDesc: `Continuing will result in losing all unsaved changes.`,
    complete: `Complete`,
    completeAndNext: `Complete & Next`,
    contract: `Contract`,
    contractSigning: `Contract Signing`,
    contractStatus: `Contract Status`,
    contractStatusDesc: `Status of contract.`,
    contractedRate: `Contracted Rate ($/kWh)`,
    monthlyBillInput: `Monthly Bill ($)`,
    monthlyBillBelowMinimum: `Monthly Bill cannot be below {{minimum}}.`,
    copyLink: `Copy Link`,
    createLink: `Create Link`,
    customerName: `Customer Name`,
    customSavingsReportFor: `Custom Savings Report for`,
    dateCreated: `Date Created`,
    deactivate: `Deactivate`,
    dealerFee: `Dealer Fee`,
    dealerFeePercentage: `Dealer Fee %`,
    default: `Default`,
    defaultStatus: `Default Status`,
    design: `Design`,
    designInProgressMessage: `A Vision designer is working on this design. Once complete, you may use the Panel Activation phase to finalize the layout and system size.`,
    designInProgressTitle: `Design In Progress`,
    designPreventionTitle: `Editing is not possible at this time`,
    designPreventionDesc: `Until the user finishes editing, you cannot edit the current design. Updates wont be saved on the server.`,
    retiredImageryUnavailableTitle: `Saved Bing image unavailable`,
    retiredImageryUnavailableDescription: `This Design uses a saved Bing image that can no longer be loaded, and Bing imagery is retired. Layout editing is paused so panels are not edited against a different background. Choose a supported image source to continue.`,
    retiredImageryGoBack: `Go back`,
    retiredImageryTryAgain: `Try again`,
    retiredImageryNoSupportedImage: `No supported image is available for this Site yet.`,
    siteImageUnavailableTitle: `Site image unavailable`,
    siteImageUnavailableDescription: `The saved aerial image for this design could not be loaded. The panel layout is shown without it.`,
    retiredImageryReplacementUnavailableTitle: `Selected image unavailable`,
    retiredImageryReplacementUnavailableDescription: `The selected image could not be loaded. Layout editing stays paused so panels are not edited against a missing background. Choose another supported image source.`,
    replaceImageKeepsLayout: `Use this image as the background? Your existing panel layout is kept as it is. Check that it lines up with the new image before saving.`,
    designWillBeReset: `WARNING: Changing the image will reset your current design and permanently delete all previous work. Please confirm if you wish to proceed.`,
    designSelection: `Design Selection`,
    disclosures: `Disclosures`,
    done: `Done`,
    downPayment: `Down Payment`,
    dragAndDropYourFilesHere: `Drag & drop your files here`,
    edit: `Edit`,
    editLoanProduct: `Edit Loan Product`,
    editUser: `Edit User`,
    electrical: `Electrical`,
    eraseLayout: `Erase Layout`,
    email: `Email`,
    energyConsumption: `Energy Consumption`,
    energyConsumptionDesc: `Enter or view historic energy consumption.`,
    energyEfficiencyKitRebate: `Energy Efficiency Kit Rebate`,
    energyOffset: `Energy Offset`,
    engineering: `Engineering`,
    enterTicketUrl: `Enter Ticket URL`,
    equipmentSelection: `Equipment Selection`,
    equipment: `Equipment`,
    escalationPercent: `esc %`,
    exportSavingsToCSV: `Export Savings Projection`,
    solarPdfGenerating: `Generating PDF`,
    solarPdfFailed: `Unable to generate the PDF. Please try again.`,
    solarPdfPopupBlocked: `Allow pop-ups for this site, then export again.`,
    exportToPdf: `Export To PDF`,
    fee: `Fee`,
    financing: `Financing`,
    financingSelection: `Financing Selection`,
    financialProduct: `Financial Product`,
    financingType: `financing type`,
    productOffering: `Product Offering`,
    productGroup: `Finance Type`,
    firstName: `First Name`,
    freight: `Freight`,
    fundingSelectionDesc: `Setup financing and equipment for the homeowner.`,
    generators: `Generators`,
    geocodeVerification: `Geocode Verification`,
    geocodeVerificationDesc: `You can move the pin on the map to update the geocode.`,
    getProposal: `Get Proposal`,
    groundMountDesc: `Ground mounts take up more space, but they are easier to access for installation and repair. They also give you greater control over the orientation of your array to maximize production.`,
    greenbuttonData: `Greenbutton Data`,
    home: `Home`,
    homeAndUtilityInfo: `Home & Utility Info`,
    homeownerInformation: `Homeowner Information`,
    homeownerInformationDesc: `Name, address, email, phone number`,
    siteInformation: `Site Information`,
    siteInformationDesc: `Homeowner, address, and incentive eligibility`,
    homeownerRebate: `Homeowner Rebate`,
    imagery: `Imagery`,
    incentives: `Incentives`,
    incentiveSource: `Incentive Source`,
    installArea: `Install Area`,
    integrationDetails: `Integration Details`,
    integrationName: `Integration Name`,
    intersecting: `Intersecting`,
    inverter: `Inverter`,
    inverterBrand: `Inverter Brand`,
    inverterManufacturer: `Inverter Manufacturer`,
    inverterModel: `Inverter Model`,
    inverterModel2: `Secondary Inverter Model (optional)`,
    inverterSelection: `Inverter Selection`,
    inverterSelectionDesc: `Please select the type of inverter you would like for this system`,
    issuedTo: `Issued To`,
    issueNewKey: `New Key`,
    isPaidAsLumpSum: `Paid as Lump Sum`,
    isSungageRebate: `Is Sungage Rebate`,
    jurisdiction: `Jurisdiction`,
    keyCount_plural: `{{count}} Keys`,
    keyCount: `{{count}} Key`,
    keyFeatures: `Key Features`,
    lastName: `Last Name`,
    lastRequestAt: `Last Request At`,
    leadAcquisition: `Lead Acquisition`,
    lifetimeUsage: `Lifetime Usage`,
    lifetimeUsageCount_plural: `{{count}} requests`,
    lifetimeUsageCount: `{{count}} request`,
    linkCopied: `Link Copied`,
    loading: `Loading...`,
    logout: `Logout`,
    message: `Message`,
    messageForHomeowner: `Enter a message to share with the homeowner`,
    meter: `Meter`,
    miscellaneous: `Miscellaneous`,
    module: `Module`,
    modules: `Modules`,
    moduleSelection: `Module Selection`,
    moduleSelectionDesc: `Use the suggested module, or select your own from the drop down list below.`,
    moduleInverterCompatibilityWarningTitle: `Incompatible equipment selection`,
    moduleInverterCompatibilityWarning: `{{module}} is incompatible with {{inverters}}. Select a compatible inverter to continue.`,
    auroraModuleInverterCompatibilityWarning: `{{module}} is incompatible with {{inverters}}. Update this equipment in Aurora, then refresh the Design in Vision.`,
    noCompatibleInverterManufacturerWarningTitle: `No compatible inverter available`,
    noCompatibleInverterManufacturerWarning: `No {{manufacturer}} inverter is compatible with {{module}}. Select a different Module or Inverter manufacturer to continue.`,
    moduleUnavailableWarningTitle: `Module unavailable`,
    moduleUnavailableWarning: `{{module}} is no longer available. Select a current module before choosing an inverter.`,
    moduleMismatchSummary: `The selected solar panels do not match the solar panels used, proceed with caution. If needed, please submit a revision request to the Vision Design Team to re-design using the new solar panels.`,
    moduleMismatchProposal: `Panel in Proposal:`,
    moduleMismatchDesign: `Panel in Design:`,
    months: `Months`,
    monthlyConsumption: `Monthly Consumption`,
    monthlyConsumptionDesc: `I know consumption for multiple months`,
    monthlyConsumptionProfile: `Monthly Consumption Profile`,
    monthlyConsumptionProfileDesc: `This estimate of monthly consumption was generated using the readings you provided.`,
    mount: `Mount`,
    mounting: `Mounting`,
    mountSelection: `Mount Selection`,
    mountSelectionDesc: `Select if you want your system on the roof of your home, on the ground, or both.`,
    mountingType: `Mounting Type`,
    mustBeAValidUrl: `Must be a valid URL`,
    needsMoreFeedback: `Needs More Feedback`,
    newCustomerIncentive: `New Customer Incentive`,
    newIntegration: `Add a New Integration`,
    next: `Next`,
    no: `No`,
    noEquipmentSelected: `No Equipment Selected`,
    noKey: `No Key`,
    noKeys: `No Previous Keys`,
    nonPrimaryEquipmentSelection: {
      part1: `Please select the type of`,
      part2: `you would like for this system.`,
    },
    notAPowurUser: `Not a Powur User`,
    numberOfDesigns: `# of Designs`,
    offset: `Offset`,
    or: `or`,
    orientation: `Orientation`,
    page: `Page`,
    panels: `Panels`,
    panelsAutoPlaced: `Panels were automatically placed`,
    panelCount: `Panel Count`,
    phoneNumber: `Phone Number`,
    postSolarPowerInstallation: `After Solar Power Installation`,
    postSolarPowerInstallationRep: `After Solar Power Installation (REP)`,
    powurMargin: `Powur Margin %`,
    powurOsId: `Powur OS ID`,
    powurProjectId: `Powur Project ID`,
    powurUser: `Powur User`,
    preSolarPowerInstallation: `Before Solar Power Installation`,
    preSolarPowerInstallationRep: `Before Solar Power Installation (REP)`,
    previous: `Previous`,
    price: `Price`,
    pricing: `Pricing`,
    primaryContact: `Primary Contact`,
    production: `Production`,
    productName: `Product Name`,
    productivity: `Productivity`,
    projectManagement: `Project Management`,
    projectService: `Project Service`,
    projectSetup: `Project Setup`,
    projectSetupMessage: `Please set up your project`,
    proposalId: `Proposal ID`,
    proposalName: `Proposal Name`,
    proposalType: `Proposal type`,
    batteryOnly: `Battery-Only`,
    solar: `Solar`,
    proposals: `Proposals`,
    proposalSettings: `Proposal Settings`,
    provider: `Provider`,
    providerName: `Display Name`,
    powurOsLoanId: `PowurOS Loan ID`,
    publish: `Publish`,
    rate: `APR`,
    ratePlan: `Rate Plan`,
    rebates: `Rebates`,
    reset: `Reset`,
    resetConfirmation: `Are you sure you want to reset the design?`,
    resetImage: `Reset Image`,
    roofing: `Roofing`,
    roofMount: `Roof Mount`,
    roofMountDesc: `Roof mounts are less expensive because they use your existing roof structure as a foundation. They also take up less room on your property.`,
    roofWork: `Roof Work`,
    roofWorkRebate: `Roof Work Rebate`,
    save: `Save`,
    saving: `Saving`,
    saved: `Saved`,
    search: `Search`,
    searchUsersPlaceholder: `Search by name or email...`,
    selectIncentives: `Select Incentives`,
    vppIncentives: `VPP Incentives`,
    selectIncentivesDesc: `The New Customer Incentive is an optional rebate from Powur to the homeowner. The incentive is valued at 12 times the initial monthly loan payment. If a GoodLeap Flex Pay 18-month or 36-month option is selected, the New Customer Incentive will cover 18 or 36 months respectively.`,
    selectAdders: `Adders Selection`,
    selectAddersDesc: `Select from the list of adders for this project`,
    selectionOverview: `Selection Overview`,
    setbacks: `Setbacks`,
    shading: `Shading`,
    share: `Share`,
    shareDesc: `Publish and share this proposal.`,
    show: `Show`,
    skip: `Skip`,
    slope: `Slope`,
    start: `Start`,
    statesActive: `States Active`,
    status: `Status`,
    storage: `Storage`,
    storageSelection: `Storage Selection`,
    storageSelectionDesc: `Please select the type of storage you would like for this system.`,
    summaryListDesc: `Everything you need to take this project to installation`,
    submit: `Submit`,
    sunroofMessage: `Sunroof shading will be calculated after panels are placed`,
    switchedOff: `Off`,
    switchedOn: `On`,
    systemDesign: `System Design`,
    systemDesignDesc: `Design the system for your homeowner.`,
    auroraDesignDesignSection: `Design`,
    auroraDesignDetailsTab: `Details`,
    auroraDesignRefreshDetailsHide: `Hide details`,
    auroraDesignRefreshDetailsShow: `What does refresh change?`,
    auroraDesignRefreshSummary: `Refreshing can clear financing selections.`,
    auroraDesignSections: `Aurora Design sections`,
    auroraDesignSyncSection: `Sync`,
    auroraSyncBlurbCompleted: `The latest sync from Aurora completed.`,
    auroraSyncBlurbInProgress: `Pulling the latest design from Aurora.`,
    auroraSyncBlurbNotRecorded: `No sync has been recorded for this design yet.`,
    auroraSyncPillCompleted: `Synced`,
    auroraSyncPillFailed: `Sync error`,
    auroraSyncPillInProgress: `Syncing`,
    auroraSyncPillNotRecorded: `Not synced`,
    auroraDesignDetails: `Aurora Design details`,
    auroraDesignDetailsLoadFailed: `Failed to load Aurora Design details.`,
    auroraDesignDetailsLoading: `Loading Aurora Design details`,
    auroraDesignRefreshAction: `Refresh`,
    auroraDesignRefreshFailed: `Unable to refresh the Aurora Design. Please try again.`,
    auroraDesignRefreshInProgress: `Refreshing...`,
    auroraDesignRefreshPrerequisite: `An Aurora Design must be linked by an administrator before it can be refreshed.`,
    auroraDesignRefreshStarted: AURORA_DESIGN_REFRESH_STARTED_CONFIRMATION,
    auroraDesignRefreshWarning: AURORA_DESIGN_REFRESH_DISCLOSURE,
    auroraDesignUuid: `UUID`,
    auroraDesignVersionActive: `Active`,
    auroraDesignVersionAdd: `Add version`,
    auroraDesignVersionAddLabel: `Add Additional designs from this Aurora project`,
    auroraDesignVersionAddPlaceholder: `Select an Aurora design`,
    auroraDesignVersionConfirm: `Use this version`,
    auroraDesignVersionConfirmTitle: `Use {{name}} as the Active Design?`,
    auroraDesignVersionLinked: `The Aurora Design Version was linked. Proposal data is being regenerated from Aurora.`,
    auroraDesignVersionLinkFailed: `Unable to link the Aurora Design Version. Please try again.`,
    auroraDesignVersionLinking: `Linking...`,
    auroraDesignVersionNoneAvailable: `Every design in this Aurora project is already a version on this proposal.`,
    auroraDesignVersionSwitch: `Switch`,
    auroraDesignVersionWarning: `The selected design is synchronized from Aurora and becomes the Active Design. Its equipment replaces the current equipment, and the selected Loan Product, financing selections, incentives, loan programs, and Quote are cleared.`,
    auroraDesignVersions: `Versions`,
    auroraDesignVersionsLoadFailed: `Failed to load the designs in this Aurora project.`,
    auroraDesignVersionsLoading: `Loading Aurora Design Versions`,
    auroraProjectId: `Aurora Project ID`,
    copyIdentifier: `Copy {{identifier}}`,
    auroraSynchronizationCompleted: `Completed`,
    auroraSynchronizationCompletedAt: `Sync Completed`,
    auroraSynchronizationFailed: `Failed`,
    auroraSynchronizationFailedAt: `Sync Failed`,
    auroraSynchronizationInProgress: `In progress`,
    auroraSynchronizationNotRecorded: `Not recorded`,
    auroraSynchronizationRetryInstruction: `The latest synchronization failed. Select Refresh to try again.`,
    auroraSynchronizationStartedAt: `Sync Started`,
    auroraSynchronizationStatus: `Sync Status`,
    auroraSynchronizationSyncing: `Syncing…`,
    annualOffset: `Annual Offset`,
    refresh: `Refresh`,
    refreshing: `Refreshing...`,
    retry: `Retry`,
    systemPlacement: `System Placement`,
    systemSize: `System Size`,
    taxStatus: `Tax Status`,
    term: `Term`,
    thisMayTakeSeveralSeconds: `This may take several seconds...`,
    toggleTransparency: `Toggle Transparency`,
    toggleSnap: `Toggle Panel Snapping`,
    totalAdders: `System Cost`,
    totalFee: `Total Dealer Fee %`,
    totalIncentives: `Total Incentives`,
    tdu: `Transmission & Distribution Utility`,
    travel: `Travel`,
    treeTrimmingRebate: `Tree Trimming Rebate`,
    type: `Type`,
    uncontained: `Uncontained`,
    updateUtilityInfo: `Update Utility Information`,
    upload: `Upload`,
    uploadedFiles: `Uploaded Files`,
    uploadUtilityBill: `Upload Utility Bill (optional)`,
    uploadUtilityBillDesc: `Used to calculate savings`,
    Permissions: `Permissions`,
    usersAdmin: `User Management`,
    utilityCompany: `Utility Company`,
    utilityCompanyAndRate: `Utility Company and Rate`,
    utilityCompanyAndRateDesc: `Select the utility company and rate plan you would like to use.`,
    warranties: `Warranties`,
    utilityInformation: `Utility Information`,
    fundingSelection: `Finance Plan`,
    fundingPartnerAndEquipment: `Finance Partner & Equipment`,
    fundingType: `Finance Partner`,
    utilityRate: `Utility Rate`,
    versions: `Versions`,
    warrantyReserve: `Warranty Reserve`,
    withSolar: `Cost With Solar`,
    yes: `Yes`,
    your: `Your`,
    zoomIn: `Zoom In`,
    zoomOut: `Zoom Out`,

    PhaseBlock_ThisIsTheLast: `The current phase is the final phase.`,
    PhaseBlock_DeadEnds: `The following nodes are dead ends:`,
    PhaseBlock_EdgesCross: `There are edges that cross each other.`,
    PhaseBlock_NoNodes: `No nodes found.`,
    PhaseBlock_NoPanels: `No panels found.`,
    PhaseBlock_InvalidPanels: `Remove or adjust invalid (red) panels to continue.`,
    PhaseBlock_NoAzimuth: `The following areas have no azimuth.`,
    PhaseBlock_IncompleteRoof: `The roof is incomplete.`,
    PhaseBlock_IncompleteEdgeDesignation: `Some roof faces do not have assigned azimuths.`,
    PhaseBlock_ModuleDataRequired: `Module data is required to proceed.`,

    PhaseDesc_ImageSelection: `Select the best image for the design.`,
    PhaseDesc_Outline: `Trace all sections of the roof and spaces for ground-mounted panels.`,
    PhaseDesc_EdgeDesignation: `Find the azimuth of each roof face by designating Eaves or Ridges`,
    PhaseDesc_RoofAttributes: `Set the shading and slope of each roof face.`,
    PhaseDesc_PanelPlacement: `Fit as many panels as possible onto the roof.`,
    PhaseDesc_PanelActivation: `Choose which panels will be included in the final design.`,

    PhaseName_ImageSelection: `Image Selection`,
    PhaseName_Outline: `Outline`,
    PhaseName_EdgeDesignation: `Edge Designation`,
    PhaseName_RoofAttributes: `Roof Attributes`,
    PhaseName_PanelPlacement: `Panel Placement`,
    PhaseName_PanelActivation: `Panel Activation`,

    EdgeType_Eave: `Eave`,
    EdgeType_Hip: `Hip`,
    EdgeType_Rake: `Rake`,
    EdgeType_Ridge: `Ridge`,
    EdgeType_Valley: `Valley`,

    //Cover
    cover: `Cover`,
    firstYearProduction: `First year production`,
    inPartnershipWith: `in partnership with`,
    powurProposal: `powur proposal`,
    presentedBy: `presented by`,
    utilitySavings: `Utility savings`,
    whyGoSolar: `Why go solar?`,
    yourSolarDesign: `Your solar design`,

    //Why Go Solar?
    controlYourEnergyCosts: `Control your energy costs`,
    whyGoSolarDes: `More people are choosing solar at home than ever before, because it
    just makes sense. There are countless reasons to choose solar, but
    we’ll just give you a few!`,
    whyGoSolarHeading: `The real question is why wouldn't you?`,
    controlYourEnergyCostsDes: `The average utility price has nearly doubled since 2003. With solar, you're no longer renting power from the local utility. Instead, you're producing solar right on your rooftop. This not only saves you money on your energy costs, but it also lets you lock in predictable rates and protect your family from rising energy rates.`,
    addValueToYourHome: `Add value to your home`,
    addValueToYourHomeDes: `Solar is one of the smartest home upgrades you can make because it saves you money in the short term AND adds considerable long-term value to your home. Studies show that homes with solar sell 20% faster and for 4-6% more on average. Think of it this way — you're giving a potential future buyer lower energy bills with zero hassle!`,
    makeADifference: `Make a difference in the world`,
    makeADifferenceDes: `Choosing clean, reliable energy right on your rooftop is one of the easiest yet most significant choices you can make as a family to be part of the solution when it comes to our environment. You'll substantially reduce your carbon footprint, which reduces pollution and helps alleviate pressure on an outdated utility infrastructure.`,

    //Why Choose Us
    whyChooseUsHeader: `We’d be honored if you chose us`,
    whyChooseUsSubHeader: `You have a lot of choices`,
    whyChooseUsDes: `If you’ll allow us a little humble brag moment, we’d like to show you
    some of the reasons that Powur is the fastest-growing solar company in
    America. These are some pretty impressive accolades, but the thing
    we’re most proud of is our 5-star ratings because it means people like
    you are happy with what we’ve done for them.`,
    experience: `Experience`,
    experienceDes: `More than 20,000 successful installations`,
    growth: `Growth`,
    growthDes: `Fastest-growing residential company in America`,
    stability: `Stability`,
    stabilityDes: `Two back to back record-breaking crowd funding rounds`,
    satisfaction: `Satisfaction`,
    satisfactionDes: `5-star rating on Yelp and A+ on BBB`,
    peace: `Peace of Mind`,
    peaceDes: `Industry-leading 30-year warranty`,
    impact: `Impact`,
    impactDes: `Supports reforestation projects in the Amazon rainforest`,
    community: `Community`,
    communityDes: `Employee and member-owned company`,
    responsibility: `Responsibility`,
    responsibilityDes: `Carbon offsets for 100% of corporate impact`,

    //What We Promise
    whatWePromiseHeader: `Here’s what we promise our customers`,
    whatWePromiseSubHeader: `Like we said, it’s all about you.`,
    whatWePromiseDes: `When you choose Powur, you can be confident knowing you are getting
    a fully customized solution for your home. Powur is unique in the
    solar industry because we are a solar platform that brings together
    the best products, services, options, and professionals for your
    home.`,
    whatOurPlatformModelMeans: `What our platform model means for you`,
    whatWePromiseCustomize: {
      text: `Our expansive equipment and financing options means you get a`,
      boldText: `fully customized solution.`,
    },
    whatWePromiseValue: {
      text1: `Our scale and low-cost operational model means affordable solutions and `,
      boldText: `the best value`,
      text2: `for you.`,
    },
    whatWePromisePartner: {
      text1: `Our team of vetted and certified installation partners means you get `,

      boldText: `local expertise `,
      text2: `and quality.`,
    },
    whatWePromisePersonal: {
      text1: `Our independent seller model means you get a `,
      boldText: `personal guide `,
      text2: `through the discovery process.`,
    },
    whatWePromisePhone: {
      text1: `Our world-class communications platform means you get `,
      boldText: `premium service`,
      text2: `through the implementation process.`,
    },
    whatWePromiseMedal: {
      text1: `Our industry leading 30-year warranty means you get`,
      boldText: `peace of mind`,
      text2: `for the life of your Powur System.`,
    },

    whatWePromiseWorld: {
      text: `Our mission-based community means you get to contribute to significant`,
      boldText: `local and global impact.`,
    },

    whatWePromiseConnect: {
      text1: `Our rewarding ambassador program means you get`,
      boldText: `financial rewards`,
      text2: `when you refer others to solar.`,
    },

    //Design For
    designedFor: `Designed for`,
    yourPowurSystem: `Your Powur solar energy system`,
    auroraDesign: `Aurora Design`,
    auroraDesignManaged: `This system design is managed in Aurora.`,
    estimatedUsage: `Estimated Usage`,
    estimatedUsageDes: `Your estimated usage is determined by your future consumption and may vary based on changes in your usage and/or changes in your weather.`,
    editDesign: `Edit Design`,
    editInAurora: `Edit in Aurora`,

    //System Production
    systemProductionSubHeading: `How much will your system produce?`,
    systemProductionHeading: `Your System Production Estimate`,
    systemProductionDes: `This chart shows the amount of energy you use at your home compared to
    the estimated amount of energy your Powur solar system will produce.`,
    offsetStatDes: `The estimated amount of your usage offset by your solar system`,
    productionStatDes: `Estimated yearly production of your solar system`,

    //Payment Summary
    autopayNotice: `APR is 0.50% higher without Autopay`,
    cash: `Cash`,
    cashPayment: `Cash Payment`,
    loanAmount: `Loan Amount`,
    netSystemCost: `Net system cost`,
    preQualify: `Pre-Qualify`,
    publicQualificationApply: `Apply now`,
    publicQualificationAskConsultant: `Your consultant will walk you through this application.`,
    publicQualificationContinue: `Continue application`,
    publicQualificationNoUrl: `We could not open the lender's application. Please try again.`,
    publicQualificationNotSubmitted: `The lender did not accept this application. Your consultant can help from here.`,
    publicQualificationOpening: `Opening…`,
    publicQualificationSubmitted: `Your application has been submitted.`,
    publicQualificationSubmittedDetail: `Your consultant will follow up with you about the result. You do not need to submit it again.`,
    publicQualificationUnavailable: `Financing options are unavailable right now. Please reload the page.`,
    qualify: `Qualify`,
    residentialTaxCredit: `Residential Renewable Energy Tax Credit`,
    sendContractDocuments: `Send contract documents`,
    totalProjectCost: `Total Project Cost`,

    // Credit Application
    addCoApplicant: `Add a Co-Applicant`,
    annualIncome: `annual income`,
    dob: `date of birth`,
    citizenshipStatus: `citizenship status`,
    city: `City`,
    coApplicant: `Co-Applicant`,
    creditAppHeader: `Credit Application`,
    enfinApplicationLoadingTitle: `Connecting you to EnFin…`,
    enfinApplicationLoadingBody: `Your credit application is loading. This may take a few seconds.`,
    emailAddress: `Email Address`,
    employer: `employer`,
    employmentDuration: `employment duration (years)`,
    employmentStatus: `employment status`,
    homeOccupancy: `home occupancy`,
    homeOwnership: `home ownership`,
    less: `Less`,
    loadingMessage: `loading`,
    middleName: `middle name or initial`,
    mortgagePayment: `mortgage payment`,
    mosaicHeader: `Credit Pre-Qualification`,
    mosaicTerms: `I agree to the Terms and Conditions listed above.`,
    mosaicSecondaryTerms: `I agree to the Terms and Conditions listed above.`,
    occupation: `occupation`,
    otherIncome: `other household income (annual)`,
    personalIncome: `Personal Income (annual)`,
    phoneType: `phone type`,
    privacyPolicy: `Privacy Policy`,
    propertyOwner: `Property Owner`,
    propertyType: `property type`,
    readMore: `Read More`,
    residencyType: `residency type`,
    reverseMortgage: `reverse mortgage`,
    secondaryApplicant: `Secondary Applicant`,
    spanishSpeaker: `Spanish Speaker`,
    ssn: `last 4 of social security number`,
    fullSsn: `SSN`,
    state: `State`,
    street: `Street`,
    yearsAtAddress: `years at address`,
    zip: `zip`,

    //Savings Over Time
    savingsOverTimeSubHeader: `How much can you save?`,
    savingsOverTimeHeader: { text1: `Save`, text2: `over 25 years` },
    monthlyBillComputeNote: `Includes the compute node's guaranteed base; see Savings Over Time`,
    savingsOverTimeComputeNote: `Includes the compute node's guaranteed base of \${{monthly}} a month in each of {{years}} years. The network bonus is never counted.`,
    cashLegendItemSystemCost: `System Cost`,
    cashLegendItemCumulativeUtilityCostWithoutSolar: `Cumulative Utility Cost Without Solar`,
    financedLegendItemWithoutSolar: `Monthly Utility Bill Without Solar`,
    financedLegendItemWithSolar: `Monthly Utility Bill With Solar`,
    financedLegendItemCost: `Monthly Solar System Cost`,
    newSolarPayment: `New Solar Payment`,
    currentUtilityPayment: `Current Utility Payment`,
    newUtilityPayment: `New Utility Payment`,
    availableIncentives: `Available Incentives`,
    costOfDoingNothing: `Cost of Doing Nothing`,
    totalSavings: `Total 25 Year Savings`,

    //We influence change
    influenceChangeSubHeader: `Why Powur?`,
    influenceChange: {
      text1: `We`,
      highlightText: `influence`,
      text2: `change`,
    },
    weInfluenceChange: `We influence change`,
    slideOneHeader: `Our missions is to accelerate the adoption of clean energy worldwide.`,
    slideOneDes: `Each customer makes a tangible difference, measurable difference in the path toward a clean energy future`,
    slideTwoHeader: `We are actively building the solar and clean energy economy in America`,
    slideTwoDes: `As both a B Corporation and a Certified B Corp, Powur takes our responsibility to our communities very seriously. These distinctions mean that we have achieved an independent B Impact score representing tangible social and environmental performance and that our corporate governance structure represents our commitment to all stakeholders beyond profits.`,
    ecologiDes: `Climate positive workforce through Ecologi partnership`,
    logoOneDes: `Empowering indigenous people in the Amazon through every project`,
    logoTwoDes: `Supporting individual families with shelter and clean energy`,
    slideThreeHeader: `Each year, Powur produces an Impact Report for the year prior. Here are some of the highlights of our 2023 report`,
    slideThreeInstalls: `Number of 2023 installs`,
    installationsTotal: `Total Installations in 2023`,
    kilowattsInstalled: `Kilowatts Installed in 2022`,
    customerSavings: `Estimated lifetime customer savings from 2023 installs`,
    savedCO2: `Estimated lifetime pounds of CO2 saved from 2023 installs`,
    slideFourHeader: `Here's what that's equivalent to`,
    vehiclesRemoved: `Passenger vehicles removed from the road for a year`,
    gasolineNotConsumed: `gallons of gasoline not consumed`,
    wasteRecycled: `trash bags of waste recycled instead of being taken to the landfill`,
    seedlingsGrown: `tree seedlings grown for 10 years`,

    //How does solar work
    whatIsSolarSubHeading: `How does solar work?`,
    whatIsSolarHeading: {
      text1: `Clean energy from the sun right on`,
      highlightText: `your roof`,
      text2: `top`,
    },
    whatIsSolarDes: `Simply put, solar uses sunlight, abundant and readily available, to power
    your home. Here are the basics of how that works and the components you
    need for a solar system.`,
    panelsDes: `Your panels absorb the light from the sun in layers of silicon to create an electric current.`,
    inverterDes: `An inverter converts the electricity from your panels from “direct current” (DC) electricity (meaning it flows only one direction) to “alternating current” (AC) electricity, which is what we use in our homes and businesses.`,
    meterDes: `Your meter keeps you connected to the grid, monitors the flow of electricity, and tracks the amount of energy you use. In areas where net metering is available your electricity can flow both into your home OR back into the grid.`,
    gridConnection: `Grid Connection & Net Metering`,
    gridConnectionDes: `Many areas provide what’s called net metering, which means that any excess power you produce on your rooftop can be sold back into the grid.`,
    storageDes: `Storage solutions are becoming increasingly more attractive for homeowners who want to store excess energy for use at a later time.`,

    //The Steps
    stepsHeader: {
      text1: `A simple,`,
      highlightText: `hassle-free`,
      text2: `process`,
    },
    stepsDes: `Once you choose to move forward with Powur you are one step closer to
    controlling your energy costs. You’ll have a dedicated Project
    Manager assigned to your project and our platform will keep you in
    the know every step of the way. Every market is different, but
    typically takes anywhere from 3-16 weeks for your panels to be
    installed and power to be flowing into your home.`,
    projKickOff: `Project Kick-off!`,
    projKickOffTitle: `Contracts Signed & Welcome Call`,
    projKickOffDes: `The process starts with the Home Improvement Contract which you can sign with the help of your Seller. Once that’s complete you'll get a Welcome Call from our Project Success Team to answer any questions you may have and go over a few details of your project.`,
    stageOne: `Stage 1 • 1-2 weeks`,
    stageOneTitle: `Site Survey`,
    stageTwo: `Stage 2 • 1-2 weeks`,
    stageTwoTitle: `Design`,
    stageThree: `Stage 3 • 4-6 weeks`,
    stageThreeTitle: `Permitting`,
    stageFour: `Stage 4 • 4-6 weeks`,
    stageFourTitle: `Installation`,
    stageFive: `Stage 5 • 1 week`,
    stageFiveTitle: `Inspection`,
    stageSix: `Stage 6 • 4-6 weeks`,
    stageSixTitle: `Powur ON!`,

    //System Components
    yourSystemComponents: `Your System Components`,

    //AI Compute
    aiCompute: `AI Compute`,
    aiComputeSlide: {
      eyebrow: `Optional add-on · Compute node`,
      title: `Compute income that can`,
      titleHighlight: `help pay your bills.`,
      description: `Host a Vendor compute node at home. Vendor pays you a fixed {{base}} every month for {{years}} years, plus a network bonus of up to {{bonusCap}} when the network earns it.`,
      descriptionNoFigures: `Host a Vendor compute node at home. Vendor pays you a fixed base every month, plus a network bonus when the network earns it.`,
      defaultNodeName: `Compute node`,
      nodeImageAlt: `Vendor compute node, wall-mounted`,
      nodeEyebrow: `Vendor® compute node`,
      partnership: `In partnership with Vendor®`,
      planPill: `{{plan}} Hosting Plan`,
      plans: {
        core: `Core`,
        plus: `Plus`,
        max: `Max`,
      },
      howItWorksEyebrow: `How it works`,
      howItWorks: SOLAR_AI_COMPUTE_HOW_IT_WORKS,
      term: `{{years}}-year term from activation · Vendor maintains and upgrades the node at no cost`,
      termNoFigures: `Term starts at activation · Vendor maintains and upgrades the node at no cost`,
      story: SOLAR_AI_COMPUTE_STORY,
      bonus: {
        ...SOLAR_AI_COMPUTE_BONUS,
        payBase: `{{amount}} base`,
        payBonusUpTo: `+ bonus up to {{amount}}`,
        payBaseNoFigures: `Your base`,
        payBonusUpToNoFigures: `+ bonus up to your plan's limit`,
        payBonusZero: `+ $0 bonus`,
      },
      ledger: {
        eyebrow: `Your monthly hosting payment`,
        plan: `Hosting plan`,
        planNote: `set in your hosting schedule`,
        base: `Fixed monthly base`,
        baseNote: `from activation, every month of the {{years}}-year term`,
        guaranteed: `Guaranteed`,
        bonus: `+ Network performance bonus`,
        bonusNote: `the most it can be · may be $0`,
        varies: `Varies`,
        nodeElectricity: `Node electricity`,
        nodeElectricityNote: `savings modeled at 90% utilization`,
        nodeElectricityValue: `{{kwh}} kWh/yr`,
        total: `Your monthly payment`,
        totalNote: `base + bonus, before taxes`,
        upTo: `up to`,
        legendBase: `{{amount}} guaranteed base`,
        legendBonus: `up to {{amount}} bonus`,
      },
      share: {
        eyebrow: `Where your payment can go · illustrative`,
        text1: `Up to`,
        text2: ` — a {{base}} base plus bonus — beside your estimated`,
        text3: `utility bill after solar.`,
        // The same words as text2 in English; Spanish orders them differently.
        costText2: ` — a {{base}} base plus bonus — beside your estimated`,
        costText3: `utility bill and solar payment.`,
        costBreakdown: `{{bill}} utility bill after solar + {{payment}} solar payment`,
        perMonth: `{{amount}}/mo`,
        coveredBill: `Toward your est. bill`,
        coveredCost: `Toward your bill + payment`,
        leftOver: `Base left over`,
        bonusUpTo: `Bonus, up to`,
        note: `An illustration. You can authorize Vendor to pay part or all of your payment toward your electricity, internet, solar, financing or battery bills; what it pays to a bill comes out of the same payment, not on top of it. Only the base is guaranteed; the bonus may be $0.`,
      },
      stats: {
        monthly: `Monthly payment`,
        upTo: `up to {{amount}}`,
        monthlyNote: `{{base}} guaranteed + up to {{bonusCap}} bonus`,
        termTotal: `{{years}}-year total`,
        termTotalNote: `{{amount}} of it guaranteed`,
        term: `Term`,
        termValue: `{{years}} yr`,
        termNote: `from activation · no-cost upgrades`,
      },
      app: {
        eyebrow: `Earnings in view`,
        text: `Track your base, bonus and payouts in the Vendor app.`,
        imageAlt: `Vendor app showing compute earnings`,
      },
      faqEyebrow: `Questions homeowners ask`,
      faq: SOLAR_AI_COMPUTE_FAQ,
      unavailable: {
        loading: `Loading the income estimate…`,
        withheld: `Income estimates are being updated`,
        failed: `Income estimate unavailable`,
      },
      disclosure: {
        intro: `Illustrative figures based on the Vendor Hosting Agreement, Revision 2.10.`,
        plan: `The {{plan}} Hosting Plan pays a fixed {{base}} monthly base from activation for the {{years}}-year service term; the variable network performance bonus is capped at {{bonusCap}} a month, is calculated from network-wide collected receipts under Section 2.2, and may be $0.`,
        planNoFigures: `Each Hosting Plan pays a fixed monthly base from activation for the service term; the variable network performance bonus is capped by plan, is calculated from network-wide collected receipts under Section 2.2, and may be $0.`,
        capNotEstimate: `Totals that include the bonus show its cap, not an estimate.`,
        savingsInclude: `Your savings estimate counts only the base, in each of your {{years}} savings years.`,
        electricity: `Node electricity is modeled at 90% utilization and included in your savings estimate. Payments may be taxable; consult a tax advisor.`,
      },
    },

    //Powur Care
    powurCare: `Powur Care`,
    powurCareSlide: {
      title: `Safeguard your solar with`,
      titleHighlight: `{{years}}-year coverage.`,
      description: `Industry-leading protection on every component we install — parts, labor, and monitoring, handled by Powur for the next three decades.`,
      badgeAlt: `Powur Care {{years}}-year warranty badge`,
      coveragePeriodEyebrow: `Coverage period`,
      coveragePeriod: `Three decades of total protection, monitored`,
      coveragePeriodEnd: `end-to-end.`,
      benefitsEyebrow: `Industry-leading protection`,
      yearsPill: `{{years}} yrs`,
      benefits: {
        partsAndLabor: {
          title: `Parts & labor covered 100%`,
          note: `End-to-end coverage on hardware AND on-site work.`,
        },
        noDeductible: {
          title: `$0 deductible`,
          note: `Never a fee to file a claim.`,
        },
        nationwide: {
          title: `Complete U.S. coverage`,
          note: `Continuous coverage if you move out-of-state.`,
        },
        transferable: {
          title: `Transferable ownership`,
          note: `Adds resale value — coverage moves with the home.`,
        },
        powurBacked: {
          title: `Powur-backed confidence`,
          note: `Backed by Powur, not a third-party warranty broker.`,
        },
        inHouseClaims: {
          title: `Claims handled in-house`,
          note: `One number, one team, no escalation tree.`,
        },
      },
      truckRollFee: `An initial diagnostic truck-roll fee may apply to the first service visit. This fee is`,
      truckRollFeeRefunded: `fully refunded`,
      truckRollFeeEnd: `if the reported issue is determined to be covered under this warranty.`,
      coveredEyebrow: `What we cover`,
      systemsPill: `{{total}} systems`,
      systems: {
        panels: `Solar panels`,
        inverters: `Microinverters`,
        battery: `Battery storage`,
        racking: `Racking system`,
        roof: `Roof penetration`,
        workmanship: `Installation workmanship`,
      },
      limitedTerms: {
        battery: {
          before: `Energy storage (battery) coverage is provided for a term of`,
          term: `twenty-five (25) years`,
          after: `, in addition to — and not in lieu of — any applicable manufacturer warranty.`,
        },
        roof: {
          before: `Roof penetration coverage is provided for a term of`,
          term: `fifteen (15) years`,
          after: `, in addition to — and not in lieu of — any applicable contractor or manufacturer warranty.`,
        },
      },
      footer: `Coverage begins on Powur On day and runs continuously for {{years}} years. See full terms at`,
    },

    // Contract Signing
    send: `Send`,
    sending: `Sending`,
    sendNew: `Send New Docs`,
    noContracts: `No Contracts have been sent.`,
    contractCannotBeSent: `Contract Cannot be Sent`,
    designNeedsCorrected: `Design Cannot Be Installed: The project cannot be installed as currently designed. Edit the design to meet the requirements.`,
    dateSent: `Sent`,
    dateSigned: `Signed`,
    totalCost: `Total Cost`,

    ...regressionWarnings,
    ...toolDescriptions,
    ...toolLabels,

    ...utilityCompanyAdminTable,

    ...poisonMessages,

    ...brochureSections,
    ...monthlyBillSlideDisclaimers,
    ...utilitySavingsSlides,
    ...systemSummarySlide,

    ...shareProposalStep,

    //Drawer
    escalate: `Escalate`,
    escalationDesc: `Please provide any additional or relevant details that are critical to your escalation being done correctly. Be specific as you can:`,
    escalationPlaceholder: `Please provide additional details relating to your escalation request. (Maximum 1000 characters)`,
    notes: `Notes`,
    proposalDetails: `Proposal Details`,
    requestEscalation: `Request Escalation`,
    thereAreNoNotesForThisDesign: `There are no notes for this design.`,
    tickets: `Tickets`,
    ticketUrl: `Ticket URL`,
    poisonedDesign: `This design is invalid and cannot be sold.`,
  },
}

export default en
